import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  FileText,
  Plus,
  Search,
  Calendar,
  User,
  Filter,
  Smile,
  Frown,
  Meh
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const moodOptions = [
  { value: 'happy', label: 'Happy', icon: Smile, color: 'text-green-500' },
  { value: 'neutral', label: 'Neutral', icon: Meh, color: 'text-amber-500' },
  { value: 'upset', label: 'Upset', icon: Frown, color: 'text-red-500' },
];

export default function DailyLogs() {
  const { user, organization } = useAuth();
  const [logs, setLogs] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newLog, setNewLog] = useState({
    client_id: '',
    log_type: 'general',
    content: '',
    mood: '',
    food_intake: '',
    fluid_intake: '',
    mobility: '',
    personal_care: '',
  });

  useEffect(() => {
    fetchData();
  }, [organization, filterDate]);

  const fetchData = async () => {
    try {
      const params = { organization_id: organization?.id };
      if (filterDate) params.date = filterDate;
      
      const [logsRes, clientsRes] = await Promise.all([
        axios.get(`${API_URL}/api/daily-logs`, { params }),
        axios.get(`${API_URL}/api/clients`, { params: { organization_id: organization?.id } })
      ]);
      
      setLogs(logsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLog = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/daily-logs`, {
        ...newLog,
        organization_id: organization?.id,
        created_by: user?.id,
      });
      toast.success('Daily log created successfully');
      setIsDialogOpen(false);
      setNewLog({
        client_id: '',
        log_type: 'general',
        content: '',
        mood: '',
        food_intake: '',
        fluid_intake: '',
        mobility: '',
        personal_care: '',
      });
      fetchData();
    } catch (error) {
      console.error('Error creating log:', error);
      toast.error('Failed to create log');
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? `${client.first_name} ${client.last_name}` : 'Unknown';
  };

  const getMoodIcon = (mood) => {
    const option = moodOptions.find(m => m.value === mood);
    if (!option) return null;
    const Icon = option.icon;
    return <Icon className={`w-5 h-5 ${option.color}`} />;
  };

  const filteredLogs = logs.filter(log => {
    const clientName = getClientName(log.client_id).toLowerCase();
    return clientName.includes(searchTerm.toLowerCase()) ||
           log.content?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="min-h-screen" data-testid="daily-logs-page">
      <Header title="Daily Logs" />
      
      <div className="p-6 space-y-6">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="log-search"
              />
            </div>
            <Input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="w-44"
              data-testid="date-filter"
            />
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700" data-testid="add-log-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Log Entry
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>New Daily Log Entry</DialogTitle>
                <DialogDescription>
                  Record daily activities, observations, and care notes
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateLog} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="client_id">Client *</Label>
                    <Select 
                      value={newLog.client_id}
                      onValueChange={(v) => setNewLog(prev => ({ ...prev, client_id: v }))}
                    >
                      <SelectTrigger data-testid="log-client">
                        <SelectValue placeholder="Select client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map(client => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.first_name} {client.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="log_type">Log Type</Label>
                    <Select 
                      value={newLog.log_type}
                      onValueChange={(v) => setNewLog(prev => ({ ...prev, log_type: v }))}
                    >
                      <SelectTrigger data-testid="log-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General</SelectItem>
                        <SelectItem value="morning">Morning</SelectItem>
                        <SelectItem value="afternoon">Afternoon</SelectItem>
                        <SelectItem value="evening">Evening</SelectItem>
                        <SelectItem value="night">Night</SelectItem>
                        <SelectItem value="handover">Handover</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Mood</Label>
                  <div className="flex gap-3">
                    {moodOptions.map((option) => (
                      <Button
                        key={option.value}
                        type="button"
                        variant={newLog.mood === option.value ? 'default' : 'outline'}
                        onClick={() => setNewLog(prev => ({ ...prev, mood: option.value }))}
                        className={newLog.mood === option.value ? 'bg-teal-600' : ''}
                        data-testid={`mood-${option.value}`}
                      >
                        <option.icon className={`w-5 h-5 mr-2 ${option.color}`} />
                        {option.label}
                      </Button>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="content">Notes *</Label>
                  <Textarea
                    id="content"
                    value={newLog.content}
                    onChange={(e) => setNewLog(prev => ({ ...prev, content: e.target.value }))}
                    rows={4}
                    placeholder="Describe activities, observations, and any concerns..."
                    required
                    data-testid="log-content"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="food_intake">Food Intake</Label>
                    <Input
                      id="food_intake"
                      value={newLog.food_intake}
                      onChange={(e) => setNewLog(prev => ({ ...prev, food_intake: e.target.value }))}
                      placeholder="e.g., Good - ate full breakfast"
                      data-testid="log-food"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fluid_intake">Fluid Intake</Label>
                    <Input
                      id="fluid_intake"
                      value={newLog.fluid_intake}
                      onChange={(e) => setNewLog(prev => ({ ...prev, fluid_intake: e.target.value }))}
                      placeholder="e.g., 500ml water, 2 cups tea"
                      data-testid="log-fluid"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="mobility">Mobility</Label>
                    <Input
                      id="mobility"
                      value={newLog.mobility}
                      onChange={(e) => setNewLog(prev => ({ ...prev, mobility: e.target.value }))}
                      placeholder="e.g., Assisted with walking frame"
                      data-testid="log-mobility"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personal_care">Personal Care</Label>
                    <Input
                      id="personal_care"
                      value={newLog.personal_care}
                      onChange={(e) => setNewLog(prev => ({ ...prev, personal_care: e.target.value }))}
                      placeholder="e.g., Full wash, dressed independently"
                      data-testid="log-personal-care"
                    />
                  </div>
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-testid="submit-log">
                    Save Log Entry
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Today's Logs</p>
                  <p className="text-3xl font-bold">
                    {logs.filter(l => new Date(l.created_at).toDateString() === new Date().toDateString()).length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-teal-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">This Week</p>
                  <p className="text-3xl font-bold">{logs.length}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Clients Logged</p>
                  <p className="text-3xl font-bold">
                    {new Set(logs.map(l => l.client_id)).size}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <User className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Logs List */}
        <Card data-testid="logs-list">
          <CardHeader>
            <CardTitle>Recent Log Entries</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-24 bg-slate-100 animate-pulse rounded-lg" />
                ))}
              </div>
            ) : filteredLogs.length > 0 ? (
              <div className="space-y-4">
                {filteredLogs.map((log) => (
                  <div 
                    key={log.id}
                    className="p-4 bg-slate-50 rounded-lg border border-slate-100"
                    data-testid={`log-entry-${log.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                          <User className="w-5 h-5 text-teal-600" />
                        </div>
                        <div>
                          <p className="font-medium">{getClientName(log.client_id)}</p>
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <Calendar className="w-3 h-3" />
                            {new Date(log.created_at).toLocaleString('en-GB')}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {log.mood && getMoodIcon(log.mood)}
                        <Badge variant="secondary" className="capitalize">
                          {log.log_type}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-slate-700 mb-3">{log.content}</p>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                      {log.food_intake && (
                        <span>🍽️ {log.food_intake}</span>
                      )}
                      {log.fluid_intake && (
                        <span>💧 {log.fluid_intake}</span>
                      )}
                      {log.mobility && (
                        <span>🚶 {log.mobility}</span>
                      )}
                      {log.personal_care && (
                        <span>🧼 {log.personal_care}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No log entries found</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setIsDialogOpen(true)}
                >
                  Create First Log Entry
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
